/** Нижний лист ОБЗОРА (Aurora, ScreenOverview прототипа): ручка, «Куда идёте?»,
 *  выбор этажа, лента чипов быстрых целей (первый POI каждой категории пресета
 *  площадки, ADR 0007) и футер с атрибуцией карты и пилюлей ИИ-подсказок.
 *
 *  Ближайшая цель получает path-градиент и расстояние «· N м» от QR-якоря:
 *  человек у щита видит не абстрактный «Туалет», а «Туалет · 40 м». */

import { haversineM } from '@app/geo'
import { formatDistance, tr } from '@app/i18n'
import { resolveProfiles } from '@app/rvf'
import { selectTarget } from '../actions'
import { anchorId, assistantOn, catalogOpen, langTick, venue } from '../state'
import { categoryLabel } from './labels'

/** Быстрая цель ленты: POI + расстояние от якоря (null — якоря нет). */
interface QuickTarget {
  readonly id: string
  readonly label: string
  readonly distanceM: number | null
}

function quickTargets(): readonly QuickTarget[] {
  const v = venue.value
  if (!v) return []
  const from = v.anchors.find((a) => a.id === anchorId.value)?.point ?? null
  return resolveProfiles(v.venue).chips.flatMap((category) => {
    const poi = v.pois.find((p) => p.category === category)
    if (!poi) return []
    return [
      {
        id: poi.id,
        label: categoryLabel(category),
        distanceM: from ? Math.round(haversineM(from, poi.point)) : null,
      },
    ]
  })
}

export function Chips() {
  langTick.value
  const v = venue.value
  if (!v) return null
  const targets = quickTargets()
  // Ближайшая цель — герой ленты (path-градиент). Без якоря «ближайшей» нет:
  // выделять первую попавшуюся значило бы соврать о расстоянии.
  const nearest = targets.reduce<QuickTarget | null>(
    (best, t) =>
      t.distanceM !== null && (best === null || t.distanceM < (best.distanceM ?? Infinity))
        ? t
        : best,
    null,
  )

  return (
    <div class="ov-sheet glass">
      <span class="ov-handle" aria-hidden="true" />
      <div class="ov-head">
        <b>{tr('sheet.where_to')}</b>
        <button
          type="button"
          class="ov-floor"
          onClick={() => {
            catalogOpen.value = !catalogOpen.value
          }}
        >
          {tr('sheet.directory')} <span aria-hidden="true">▾</span>
        </button>
      </div>

      {targets.length > 0 && (
        <nav class="chips">
          {targets.map((t) => (
            <button
              key={t.id}
              type="button"
              class={`chip${t.id === nearest?.id ? ' chip-path' : ''}`}
              onClick={() => selectTarget(t.id)}
            >
              {t.label}
              {t.id === nearest?.id && t.distanceM !== null && ` · ${formatDistance(t.distanceM)}`}
            </button>
          ))}
        </nav>
      )}

      <div class="ov-foot">
        <span>{tr('map.attribution')}</span>
        {/* Пилюля «ИИ подсказки» — только когда ассистент площадки подключён:
            обещать помощь, которой нет, нечестно. */}
        {assistantOn.value && <span class="pill pill-iris">{tr('sheet.ai_hints')}</span>}
      </div>
    </div>
  )
}
